//@flow
import React from 'react'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Package from '../../../package.json'

const useStyles = makeStyles((theme) => ({
  footer: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(212, 175, 55, 0.15)'
      : '1px solid rgba(212, 175, 55, 0.2)',
    padding: theme.spacing(6, 2),
    marginTop: 'auto',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.5), transparent)',
      opacity: 0.5,
    }
  },
  brandSection: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginBottom: 0,
    },
  },
  brandTitle: {
    fontFamily: '"Outfit", sans-serif',
    fontWeight: 700,
    fontSize: '1.5rem',
    background: 'linear-gradient(135deg, #CA9A2C 0%, #F3C817 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing(1),
    display: 'inline-block',
  },
  brandTagline: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    maxWidth: '300px',
    lineHeight: 1.6,
  },
  columnTitle: {
    fontFamily: '"Outfit", sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    position: 'relative',
    display: 'inline-block',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -4,
      left: 0,
      width: 24,
      height: 2,
      background: '#CA9A2C',
      borderRadius: 1,
    }
  },
  linkList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  linkItem: {
    marginBottom: theme.spacing(1.5),
  },
  footerLink: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      color: '#CA9A2C',
      textDecoration: 'none',
      transform: 'translateX(4px)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -2,
      left: 0,
      right: 0,
      height: 1,
      background: 'linear-gradient(90deg, transparent, currentColor, transparent)',
      transform: 'scaleX(0)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover::after': {
      transform: 'scaleX(1)',
    },
  },
  bottomBar: {
    marginTop: theme.spacing(6),
    paddingTop: theme.spacing(3),
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.05)'
      : '1px solid rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  copyright: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
  },
  version: {
    color: theme.palette.text.disabled,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
    },
  }
}));

function AppFooter() {
  const classes = useStyles()
  const currentYear = new Date().getFullYear()

  return (
    <footer className={classes.footer}>
      <Grid container spacing={4} justify="space-between">
        {/* Brand Column */}
        <Grid item xs={12} md={4}>
          <div className={classes.brandSection}>
            <Typography className={classes.brandTitle}>
              Gigsdata
            </Typography>
            <Typography className={classes.brandTagline}>
              Empowering the future of work with intelligent data solutions and seamless crowd collaboration.
            </Typography>
          </div>
        </Grid>

        {/* Quick Links Column */}
        <Grid item xs={6} md={2}>
          <Typography className={classes.columnTitle}>
            Platform
          </Typography>
          <ul className={classes.linkList}>
            <li className={classes.linkItem}>
              <Link href="/work" className={classes.footerLink}>Find Work</Link>
            </li>
            <li className={classes.linkItem}>
              <Link href="/learn" className={classes.footerLink}>Learn</Link>
            </li>
            <li className={classes.linkItem}>
              <Link href="/qualify" className={classes.footerLink}>Qualify</Link>
            </li>
          </ul>
        </Grid>

        {/* Legal Column */}
        <Grid item xs={6} md={2}>
          <Typography className={classes.columnTitle}>
            Legal
          </Typography>
          <ul className={classes.linkList}>
            <li className={classes.linkItem}>
              <Link href="/privacy.html" className={classes.footerLink}>Privacy Policy</Link>
            </li>
            <li className={classes.linkItem}>
              <Link href="https://gigsdata.com/termOfService" className={classes.footerLink}>Terms of Service</Link>
            </li>
          </ul>
        </Grid>

        {/* Company Column */}
        <Grid item xs={12} md={3}>
          <Typography className={classes.columnTitle}>
            Company
          </Typography>
          <ul className={classes.linkList}>
            <li className={classes.linkItem}>
              <Link href="https://gigsdata.com" className={classes.footerLink}>About Us</Link>
            </li>
            <li className={classes.linkItem}>
              <Typography variant="body2" style={{ color: 'inherit', fontSize: '0.9rem', opacity: 0.8 }}>
                Operating Partner:<br />Dataswarm LLC
              </Typography>
            </li>
          </ul>
        </Grid>
      </Grid>

      {/* Bottom Bar */}
      <div className={classes.bottomBar}>
        <Typography className={classes.copyright}>
          © {currentYear} <Link href="https://gigsdata.com/" color="inherit" style={{ fontWeight: 600 }}>LESOFI LLP</Link>. All rights reserved.
        </Typography>
        <Typography className={classes.version}>
          v{Package.version}
        </Typography>
      </div>
    </footer>
  )
}

export default React.memo < {} > (AppFooter)